import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, CircularProgress, Box, IconButton, Tooltip } from '@material-ui/core';
import { Fullscreen, FullscreenExit, OpenInNew as OpenInNewIcon } from '@material-ui/icons';
import { apiGet } from '../../common/amplifyApi';

// styles
import useStyles from './styles-openplan';

// components
import { Typography, Button } from '../../components/Wrappers/Wrappers';
import Widget from '../../components/Widget/Widget';

// config
import config from '../../config';

const apiName = config.apiName;

const OpenPlan = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [planUrl, setPlanUrl] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fullscreenContainerRef = useRef(null);

    useEffect(() => {
        const openTreatmentPlan = async () => {
            // Extract parameters from query string
            const queryString = new URLSearchParams(location.search);
            const orderId = queryString.get('orderId');
            const planId = queryString.get('planId');

            // Validate query parameters
            if (!orderId || !planId) {
                setError('Missing required parameters. Both order ID and plan ID are required.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                setError(null);

                // Make API call to open the treatment plan
                const response = await apiGet(apiName, `/v2/orders/${orderId}/open-plan/${planId}`);

                // Extract URL from response and display it in an iframe
                if (response && response.planUrl) {
                    // Store the URL to display in iframe instead of navigating away
                    setPlanUrl(response.planUrl);
                    setLoading(false);
                } else {
                    throw new Error('No URL provided in the response');
                }
            } catch (err) {
                console.error('Error opening treatment plan:', err);
                setError('Failed to open the treatment plan. Please try again.');
                setLoading(false);
            }
        };

        openTreatmentPlan();
    }, [location.search]);

    const handleBack = () => {
        history.goBack();
    };

    // Sync state when the user exits native fullscreen via Escape or an OS gesture
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                setIsFullscreen(false);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Once the fullscreen container is mounted, request native fullscreen
    useEffect(() => {
        if (!isFullscreen || !fullscreenContainerRef.current) return;
        const el = fullscreenContainerRef.current;
        const requestFS = el.requestFullscreen || el.webkitRequestFullscreen;
        if (requestFS) requestFS.call(el).catch(() => {});
    }, [isFullscreen]);

    const enterFullscreen = () => {
        setIsFullscreen(true);
    };

    const exitFullscreen = () => {
        const exitFS = document.exitFullscreen || document.webkitExitFullscreen;
        if (exitFS && (document.fullscreenElement || document.webkitFullscreenElement)) {
            exitFS.call(document).catch(() => {});
        }
        setIsFullscreen(false);
    };

    const handleOpenInNewTab = () => {
        window.open(planUrl, '_blank', 'noopener,noreferrer');
    };

    // Show loading state
    if (loading) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Widget>
                        <Grid item justifyContent="center" container>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                width="100%"
                                minHeight={300}
                                className={classes.layoutContainer}
                            >
                                <CircularProgress color="primary" size={60} style={{ marginBottom: 24 }} />
                                <Typography variant="h6" weight="medium" className={classes.layoutText}>
                                    Opening treatment plan...
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ marginTop: 8, textAlign: 'center' }}
                                >
                                    Please wait while we collect your treatment plan information.
                                </Typography>
                            </Box>
                        </Grid>
                    </Widget>
                </Grid>
            </Grid>
        );
    }

    // Show error state
    if (error) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Widget>
                        <Grid item justifyContent="center" container>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                width="100%"
                                minHeight={300}
                                className={classes.layoutContainer}
                            >
                                <Typography
                                    variant="h6"
                                    weight="medium"
                                    color="error"
                                    style={{ marginBottom: 16, textAlign: 'center' }}
                                >
                                    Unable to Open Treatment Plan
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                    style={{ marginBottom: 24, textAlign: 'center', maxWidth: 400 }}
                                >
                                    {error}
                                </Typography>
                                <Button variant="contained" color="secondary" onClick={handleBack}>
                                    Back to Order
                                </Button>
                            </Box>
                        </Grid>
                    </Widget>
                </Grid>
            </Grid>
        );
    }

    // Show the treatment plan in an iframe
    if (planUrl) {
        return (
            <>
                {!isFullscreen && (
                    <Widget>
                        <Box display="flex" flexDirection="column" height="calc(100vh - 230px)" style={{ padding: 0 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                                <Button variant="outlined" color="secondary" onClick={handleBack} disableRipple>
                                    Back
                                </Button>
                                <Box display="flex" style={{ gap: '12px' }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={enterFullscreen}
                                        disableRipple
                                    >
                                        <Tooltip title="Expand to Fullscreen" arrow>
                                            <Fullscreen />
                                        </Tooltip>
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleOpenInNewTab}
                                        startIcon={<OpenInNewIcon />}
                                        disableRipple
                                    >
                                        Open Plan
                                    </Button>
                                </Box>
                            </Box>
                            <Box
                                style={{
                                    flex: 1,
                                    width: '100%',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                }}
                            >
                                <iframe
                                    src={planUrl}
                                    title="Treatment Plan"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        display: 'block',
                                    }}
                                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
                                    allow="clipboard-write; fullscreen"
                                />
                            </Box>
                        </Box>
                    </Widget>
                )}
                {isFullscreen && (
                    <Box ref={fullscreenContainerRef} className={classes.fullscreenContainer}>
                        <Box className={classes.fullscreenIframeWrapper}>
                            <Tooltip title="Exit Fullscreen" arrow>
                                <IconButton
                                    onClick={exitFullscreen}
                                    className={classes.fullscreenExitButton}
                                    aria-label="Exit Fullscreen"
                                >
                                    <FullscreenExit />
                                </IconButton>
                            </Tooltip>
                            <iframe
                                src={planUrl}
                                title="Treatment Plan"
                                className={classes.fullscreenIframe}
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
                                allow="clipboard-write; fullscreen"
                            />
                        </Box>
                    </Box>
                )}
            </>
        );
    }

    return null;
};

export default OpenPlan;
